import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar, Avatar, Badge, Box, Divider, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Menu, MenuItem, Popover, Stack, Toolbar, Tooltip, Typography
} from '@mui/material';
import {
  AccountBalanceWallet, Assessment, BadgeOutlined, Business, Category, ChevronLeft,
  Dashboard, DarkMode, Factory, Fullscreen, FullscreenExit, Groups, Inventory2,
  LightMode, Menu as MenuIcon, NotificationsNone, Palette, PointOfSale, PriceCheck, QrCodeScanner, School,
  ColorLens, Settings, Storefront, Straighten, Tune, Warehouse, Work
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useThemeMode } from '../contexts/ThemeModeContext.jsx';
import { useToast } from '../contexts/ToastContext.jsx';
import { themeColorPresets } from '../theme/themeColors.js';

const width = 264;
const menu = [
  ['Dashboard', '/', Dashboard],
  ['Employees', '/employees', BadgeOutlined],
  ['Customers', '/customers', Groups],
  ['Suppliers', '/suppliers', Storefront],
  ['Schools', '/schools', School],
  ['Orders', '/orders', PointOfSale],
  ['Stitcher Scan', '/stitcher-scan', QrCodeScanner],
  ['Production', '/production', Factory],
  ['Inventory', '/inventory', Warehouse],
  ['Products', '/products', Category],
  ['Departments', '/departments', Business],
  ['Designations', '/designations', Work],
  ['Expense Heads', '/expenses', AccountBalanceWallet],
  ['Costing', '/costing', PriceCheck],
  ['Reports', '/reports', Assessment]
];
const utilityMenu = [
  ['Cities', '/cities', Business],
  ['Areas', '/areas', Category],
  ['Categories', '/categories', Category],
  ['Sizes', '/sizes', Straighten],
  ['Colors', '/colors', ColorLens]
];
const systemMenu = [
  ['Administration', '/administration', Tune],
  ['Settings', '/settings', Settings]
];

export default function AppLayout() {
  const [open, setOpen] = useState(true);
  const [anchor, setAnchor] = useState(null);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { mode, toggleMode, primaryColor, setPrimaryColor } = useThemeMode();
  const toast = useToast();

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch {
      toast.error('Fullscreen is not available in this browser');
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', zIndex: 1300 }}>
        <Toolbar>
          <IconButton onClick={() => setOpen((value) => !value)}>{open ? <ChevronLeft /> : <MenuIcon />}</IconButton>
          <Inventory2 color="primary" sx={{ ml: 1, mr: 1.5 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>GarmentERP</Typography>

          <Tooltip title={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
            <IconButton onClick={toggleMode} aria-label="Toggle theme">
              {mode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Theme color">
            <IconButton onClick={(event) => setColorAnchor(event.currentTarget)} aria-label="Change theme color">
              <Palette />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen'}>
            <IconButton onClick={toggleFullscreen} aria-label="Toggle full screen">
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Notifications">
            <IconButton><Badge color="error" variant="dot"><NotificationsNone /></Badge></IconButton>
          </Tooltip>
          <IconButton onClick={(event) => setAnchor(event.currentTarget)}>
            <Avatar sx={{ width: 34, height: 34 }}>{user?.name?.[0] || 'U'}</Avatar>
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            <MenuItem disabled>{user?.name} · {user?.role}</MenuItem>
            <Divider />
            <MenuItem onClick={logout}>Sign out</MenuItem>
          </Menu>
          <Popover
            open={Boolean(colorAnchor)}
            anchorEl={colorAnchor}
            onClose={() => setColorAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, width: 260 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.25 }}>Theme color</Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1.5 }}>
                {themeColorPresets.map((preset) => {
                  const selected = primaryColor.toLowerCase() === preset.color.toLowerCase();
                  return (
                    <Tooltip key={preset.id} title={preset.label}>
                      <Box
                        component="button"
                        type="button"
                        aria-label={preset.label}
                        onClick={() => {
                          setPrimaryColor(preset.color);
                          setColorAnchor(null);
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          border: selected ? '2px solid' : '2px solid transparent',
                          borderColor: selected ? 'text.primary' : 'transparent',
                          bgcolor: preset.color,
                          cursor: 'pointer',
                          p: 0,
                          outline: selected ? '2px solid' : 'none',
                          outlineColor: 'background.paper',
                          outlineOffset: -4
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  component="input"
                  type="color"
                  value={primaryColor}
                  onChange={(event) => setPrimaryColor(event.target.value)}
                  aria-label="Custom theme color"
                  sx={{
                    width: 40,
                    height: 32,
                    border: 0,
                    p: 0,
                    bgcolor: 'transparent',
                    cursor: 'pointer'
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Custom color
                </Typography>
              </Stack>
            </Box>
          </Popover>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open} sx={{
        width: open ? width : 72, flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? width : 72,
          mt: 8,
          borderRight: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          height: 'calc(100vh - 64px)',
          transition: 'width .2s'
        }
      }}>
        <List sx={{ px: 1, pb: 2 }}>
          {menu.map(([label, to, Icon]) => (
            <ListItemButton key={to} component={Link} to={to} selected={pathname === to}
              sx={{ borderRadius: 2, mb: .4, minHeight: 46, px: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 45 }}><Icon /></ListItemIcon>
              <ListItemText primary={label} sx={{ opacity: open ? 1 : 0, whiteSpace: 'nowrap' }} />
            </ListItemButton>
          ))}
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 1.5, py: 0.75, fontWeight: 700, letterSpacing: 1, opacity: open ? 1 : 0 }}
          >
            UTILITY
          </Typography>
          {utilityMenu.map(([label, to, Icon]) => (
            <ListItemButton key={to} component={Link} to={to} selected={pathname === to}
              sx={{ borderRadius: 2, mb: .4, minHeight: 46, px: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 45 }}><Icon /></ListItemIcon>
              <ListItemText primary={label} sx={{ opacity: open ? 1 : 0, whiteSpace: 'nowrap' }} />
            </ListItemButton>
          ))}
          <Divider sx={{ my: 1 }} />
          {systemMenu.map(([label, to, Icon]) => (
            <ListItemButton key={to} component={Link} to={to} selected={pathname === to}
              sx={{ borderRadius: 2, mb: .4, minHeight: 46, px: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 45 }}><Icon /></ListItemIcon>
              <ListItemText primary={label} sx={{ opacity: open ? 1 : 0, whiteSpace: 'nowrap' }} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, pt: 10, px: { xs: 1, md: 2 }, pb: 5 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
